import React from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import is_null from "../../helpers/is_null";
import moment from "../../helpers/moment";
import PageTitle from "../../components/PageTitle";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/Alert";
import { Card } from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import { FiBell, FiUser } from "react-icons/fi";

const Notifications = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["msNotificationSeen"],
    queryFn: async () => {
      const req = await axiosget(`ms_notification/dash`);
      if (req?.type === "error") {
        throw new Error(req?.message || "Failed to load notifications");
      }
      return req?.data;
    },
  });

  const hasError = error || data?.type !== undefined;
  const errorMessage = error?.message || data?.message || "Something went wrong";
  const notifications = !hasError && Array.isArray(data) ? data : [];

  return (
    <div>
      <PageTitle title="Notifications" sub="View notifications from lecturers and respond to them" />

      {isLoading && (
        <div className="space-y-4 mt-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex gap-4 items-start">
              <Skeleton className="w-12 h-12 flex-shrink-0" variant="circular" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      )}

      {hasError && (
        <Alert variant="danger" className="mt-4">
          <AlertTitle>Error Encountered</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {!isLoading && !hasError && notifications.length === 0 && (
        <EmptyState
          variant="no-data"
          title="No Notifications"
          description="You don't have any notifications at the moment. Check back later."
          icon={<FiBell className="h-12 w-12" />}
        />
      )}

      {notifications.length > 0 && (
        <div className="space-y-3 mt-4">
          {notifications.map((v, i) => (
            <Card key={v._id || i} padding="none" className="hover:shadow-md transition-shadow">
              <div className="flex gap-4 p-5">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {v?.staff?.photo ? (
                    <img
                      src={v.staff.photo}
                      alt={`${v?.staff?.firstname || 'Staff'}`}
                      className="w-11 h-11 rounded-full object-cover border-2 border-neutral-100"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-11 h-11 rounded-full bg-primary-100 items-center justify-center ${v?.staff?.photo ? 'hidden' : 'flex'}`}>
                    <FiUser className="w-5 h-5 text-primary-600" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-primary-700 uppercase truncate">
                      {v?.staff?.firstname} {v?.staff?.middlename} {v?.staff?.lastname}
                      {v?.results?.code && (
                        <span className="text-neutral-400 font-normal ml-1">
                          | {v?.results?.code} {v?.results?.name}
                        </span>
                      )}
                    </p>
                    <span className="text-xs text-neutral-400 whitespace-nowrap flex-shrink-0">
                      {moment(v?.createdAt)} ago
                    </span>
                  </div>
                  <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed">{v?.post}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
